import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Grid, Typography, Divider, Link, Box } from '@material-ui/core';
import { Facebook, Instagram, LinkedIn } from '@material-ui/icons';

const TiktokIcon = () => (
  <svg viewBox="0 0 24 24" width="24px" height="24px" fill="#fff">
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
  </svg>
);

const useStyles = makeStyles((theme) => ({
  '@global': {
    '@import': 'url(\'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Poppins:wght@400;500;600&display=swap\')',
  },
  footerWrapper: {
    backgroundColor: '#000',
    width: '100vw',
    marginLeft: 'calc(-50vw + 50%)',
    overflowX: 'hidden',
  },
  footerContainer: {
    maxWidth: '1280px',
    margin: '0 auto',
    padding: theme.spacing(6, 3),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(4, 2),
    },
  },
  logoContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      alignItems: 'center',
      textAlign: 'center',
    },
  },
  logo: {
    width: 120,
    marginBottom: theme.spacing(1),
  },
  companyTitle: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '1.2rem',
    marginBottom: theme.spacing(0.5),
    fontFamily: "'Poppins', sans-serif",
  },
  companySubtitle: {
    color: '#aaa',
    fontSize: '0.9rem',
    lineHeight: 1.3,
    fontFamily: "'Poppins', sans-serif",
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    fontSize: '0.95rem',
    color: '#ff7704',
    whiteSpace: 'nowrap',
    fontFamily: "'Poppins', sans-serif",
  },
  sectionLink: {
    display: 'block',
    color: '#fff',
    marginBottom: theme.spacing(1),
    textDecoration: 'none',
    fontWeight: 400,
    fontFamily: "'Inter', sans-serif",
    fontSize: '0.85rem',
    '&:hover': {
      color: '#ff7704',
    },
  },
  socialIcon: {
    marginRight: theme.spacing(1.5),
    color: '#fff',
    fontSize: '1.5rem',
    '&:hover': {
      color: '#ff7704',
    },
    '&:last-child': {
      marginRight: 0,
    },
  },
  divider: {
    backgroundColor: '#ff7704',
    margin: '2px 0 0 0',
    height: 2,
  },
  copyright: {
    color: '#fff',
    fontSize: '0.9rem',
    fontFamily: "'Poppins', sans-serif",
    textAlign: 'center',
  },
  legalLinks: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      flexWrap: 'wrap',
    },
  },
  legalLink: {
    color: '#fff',
    fontSize: '0.9rem',
    fontFamily: "'Poppins', sans-serif",
    textDecoration: 'none',
    '&:hover': {
      color: '#ff7704',
    },
  },
  socialContainer: {
    display: 'flex',
    margin: '16px 0 2px 0',
    justifyContent: 'flex-start',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
  },
  sectionContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  sectionItem: {
    flex: '1 1 auto',
    minWidth: 0,
    paddingRight: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      paddingRight: 0,
      width: '100%',
      textAlign: 'center',
      marginBottom: theme.spacing(2),
    },
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  bottomRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      textAlign: 'center',
      gap: theme.spacing(1),
    },
  },
  getStartedTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    fontSize: '0.95rem',
    color: '#ff7704',
    whiteSpace: 'nowrap',
    fontFamily: "'Poppins', sans-serif",
    marginTop: theme.spacing(2),
  },
}));

const Footer = () => {
  const classes = useStyles();

  return (
    <div className={classes.footerWrapper}>
      <div className={classes.footerContainer}>
        <Grid container spacing={5}>
          <Grid item xs={12} md={3} className={classes.leftColumn}>
            <div className={classes.logoContainer}>
              <img src="/images/ulapaap.png" alt="Company Logo" className={classes.logo} />
              <Typography variant="h6" className={classes.companyTitle}>
                Redefine Accounting Integration
              </Typography>
              <Typography variant="body2" className={classes.companySubtitle}>
                Let UlapBiz do the work,<br />
                So you can focus on what matters most.
              </Typography>
            </div>

            <Box className={classes.socialContainer}>
              <Link href="#" className={classes.socialIcon}><Facebook /></Link>
              <Link href="#" className={classes.socialIcon}><Instagram /></Link>
              <Link href="#" className={classes.socialIcon}><TiktokIcon /></Link>
              <Link href="#" className={classes.socialIcon}><LinkedIn /></Link>
            </Box>
          </Grid>

          <Grid item xs={12} md={9}>
            <div className={classes.sectionContainer}>
              <div className={classes.sectionItem}>
                <Typography variant="h4" className={classes.sectionTitle}>Products</Typography>
                <Link href="#" className={classes.sectionLink}>UlapBiz ERP</Link>
                <Link href="#" className={classes.sectionLink}>UlapPayroll</Link>
                <Link href="#" className={classes.sectionLink}>UlapInventory</Link>
                <Link href="#" className={classes.sectionLink}>UlapExpense</Link>
                <Link href="#" className={classes.sectionLink}>UlapPortal</Link>
                <Link href="#" className={classes.sectionLink}>UlapSign</Link>
                <Link href="#" className={classes.sectionLink}>UlapCRM</Link>
              </div>

              <div className={classes.sectionItem}>
                <Typography variant="h4" className={classes.sectionTitle}>Career-Specific</Typography>
                <Link href="#" className={classes.sectionLink}>UlapBiz for Accountants</Link>
                <Link href="#" className={classes.sectionLink}>UlapBiz for Employees</Link>
                <Link href="#" className={classes.sectionLink}>UlapBiz for Supervisors</Link>
                <Link href="#" className={classes.sectionLink}>UlapBiz for Managers</Link>
                <Link href="#" className={classes.sectionLink}>UlapBiz for Inventory Clerks</Link>
              </div>

              <div className={classes.sectionItem}>
                <Typography variant="h4" className={classes.sectionTitle}>Industry-Specific</Typography>
                <Link href="#" className={classes.sectionLink}>UlapBiz for Construction</Link>
                <Link href="#" className={classes.sectionLink}>UlapBiz for Motorcycle Dealers</Link>
                <Link href="#" className={classes.sectionLink}>UlapBiz for Distribution</Link>
                <Link href="#" className={classes.sectionLink}>UlapBiz for E-Commerce</Link>
              </div>

              <div className={classes.sectionItem}>
                <Typography variant="h4" className={classes.sectionTitle}>Resources</Typography>
                <Link href="#" className={classes.sectionLink}>Learn with UlapBiz</Link>
                <Link href="#" className={classes.sectionLink}>Modules & Training</Link>
                <Link href="#" className={classes.sectionLink}>Customer Stories</Link>
              </div>

              <div className={classes.sectionItem}>
                <Typography variant="h4" className={classes.sectionTitle}>Community</Typography>
                <Link href="#" className={classes.sectionLink}>Become a Solutions Partner</Link>
                <Link href="#" className={classes.sectionLink}>Become a Tech Partner</Link>
                <Link href="#" className={classes.sectionLink}>Become a School Partner</Link>
                <Typography variant="h4" className={classes.getStartedTitle}>Get Started</Typography>
                <Link href="#" className={classes.sectionLink}>Sign In</Link>
                <Link href="#" className={classes.sectionLink}>Contact Sales</Link>
              </div>
            </div>
          </Grid>
        </Grid>

        <Divider className={classes.divider} />

        <Box className={classes.bottomRow}>
          <Typography variant="body2" className={classes.copyright}>
            © {new Date().getFullYear()} Radztech Corporation | All rights reserved.
          </Typography>
          <Box className={classes.legalLinks}>
            <Link href="#" className={classes.legalLink}>Terms of Service</Link>
            <Link href="#" className={classes.legalLink}>Privacy</Link>
            <Link href="#" className={classes.legalLink}>Cookies</Link>
          </Box>
        </Box>
      </div>
    </div>
  );
};

export default Footer;
